// A screen with a board on it: the board, a row under it for the line, a
// sheet to read, and a bar of controls.
//
// Everything pressed again and again has a fixed home that nothing else can
// move, and is on the screen together with the whole board. Text is what
// varies — the evaluation, the explanation, the answer to a wrong move — and
// it lives in the sheet, which scrolls on its own and never pushes a
// control. On a phone the screen is fixed to the window: the board under the
// top bar, the bar in the tab bar's place, the sheet between. On a wide
// screen the board, the line row and the bar are one column, the sheet the
// other. See SCREENS.md.

import { useEffect, useState, type ReactNode } from "react";
import { copyText } from "../app/copy.ts";
import { Icon } from "../app/icons.tsx";
import { prefs } from "../app/prefs.ts";
import { useShell } from "../app/App.tsx";
import { Menu, type MenuItem } from "../app/ui.tsx";
import { pgnOf, type Shown } from "../board/share.ts";
import "./board-screen.css";

export function BoardScreen({
  board,
  line,
  bar,
  sheet,
  className,
  hidden,
}: {
  board: ReactNode;
  /** One row of fixed height under the board: what is happening, or the
   *  line's own controls once it is over. */
  line: ReactNode;
  /** The controls, in fixed slots, left to right. */
  bar: ReactNode;
  sheet: ReactNode;
  className?: string;
  hidden?: boolean;
}) {
  const shell = useShell();
  useEffect(() => {
    if (hidden) return;
    shell.setBoardMode(true);
    return () => shell.setBoardMode(false);
  }, [hidden, shell]);
  return (
    <section className={"board-screen" + (className ? " " + className : "")} hidden={hidden}>
      <div className="board-col">
        {board}
        <div className="board-line">{line}</div>
        <div className="puzzle-actions bar">{bar}</div>
      </div>
      <div className="board-sheet">{sheet}</div>
    </section>
  );
}

/**
 * The last slot of every bar: what the screen can do that is not pressed
 * again and again, and on every board the two ways a position leaves the
 * app — the position itself as a FEN, and the moves that reached it as a
 * PGN, each of which any other chess program reads.
 *
 * What is on the board is asked for when the menu is pressed, not while it
 * sits there, so it is whatever the board shows at that moment: the puzzle
 * mid-solve, a variation walked into, a move tried off the line.
 */
export function BoardMenu({ shown, items }: { shown: () => Shown; items?: (MenuItem | null)[] }) {
  return (
    <Menu
      className="bar-menu"
      items={[
        {
          label: "Copy the position (FEN)",
          title: "The position on the board, as a FEN",
          onClick: () => void copyText(shown().fen, "The position on the board is copied, as a FEN."),
        },
        {
          label: "Copy the moves (PGN)",
          title: "How the board got here, as a PGN",
          onClick: () => void copyText(pgnOf(shown()), "How the board got here is copied, as a PGN."),
        },
        ...(items || []),
      ]}
    />
  );
}

/** Things worth knowing about the app, shown while a board is on its way. */
const TIPS = [
  "Every position here is one you actually reached, in one of your own games.",
  "Play a wrong move and it is played out, so you see what it runs into — then Retry.",
  "Hint marks the piece that moves. Press again for the move, and once more to hide it.",
  "Once solved, the board stays live: step back into the line and try your own idea.",
  "Continue asks the engine to go on from any position on the board.",
  "A tag like “fork” or “pin” trains just that motif when you press it.",
  "Games in your repertoire tell you where you left your line: see Openings.",
  "The bar under the board shows the position from White's side, always.",
];

/**
 * A board screen before its board: the shape of the real one — the board in
 * the chosen colours, the line row, the bar with its buttons in their places —
 * so that when the puzzle arrives nothing moves, only what is in it. The page
 * draws the same thing before the app's code has run (index.html).
 */
export function BoardPlaceholder({ lead, slots, status }: { lead: ReactNode; slots: ReactNode; status: string }) {
  // The first tip is the one the page draws before the app runs (index.html).
  const [tip, setTip] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTip((n) => (n + 1) % TIPS.length), 4000);
    return () => clearInterval(timer);
  }, []);
  const colours = BOOT_COLOURS[prefs.board] || BOOT_COLOURS.default!;
  return (
    <BoardScreen
      className="board-placeholder"
      board={
        <div className="board-zone">
          <div className="board boot-board" style={{ ["--boot-light" as string]: colours[0], ["--boot-dark" as string]: colours[1] }} />
          <div className="score">
            <div className="score-bar">
              <div className="score-white" style={{ width: "50%" }} />
            </div>
            <span className="score-num">…</span>
          </div>
        </div>
      }
      line={<span className="board-status boot-status">{status}</span>}
      bar={
        <>
          {lead}
          <button type="button" className="btn hint-btn" disabled>
            <Icon name="eye" />
            Hint
          </button>
          {slots}
          <details className="menu bar-menu">
            <summary className="btn" aria-label="More">
              ⋯
            </summary>
          </details>
        </>
      }
      sheet={
        <>
          <p className="boot-tip" key={tip}>
            {TIPS[tip]}
          </p>
          <div className="boot-lines">
            <span />
            <span />
            <span />
          </div>
        </>
      }
    />
  );
}

/** Each board style's two square colours, as the placeholder draws them. */
const BOOT_COLOURS: Record<string, [string, string]> = {
  default: ["#ecdab9", "#c5a076"],
  green: ["#e0ddcc", "#4c946a"],
  blue: ["#d8ecfb", "#86afcf"],
  "chess-club": ["#e6d3b1", "#af6b3f"],
  "chessboard-js": ["#f0d9b5", "#b58863"],
};
